/**
 * Make authenticated API request
 * @param {string} endpoint - API endpoint
 * @param {object} options - Fetch options
 * @returns {Promise} API response
 */
const apiRequest = async (endpoint, options = {}, token) => {
    try {
        const api_endpoint = process.env.REACT_APP_API_ENDPOINT;

        const url = endpoint.startsWith('http') ? endpoint : `${ api_endpoint }${ endpoint }`;

        const config = {
            headers: {
                'Content-Type': 'application/json',
                ...(token && { 'Authorization': `Bearer ${ token }` }),
                ...options.headers
            },
            ...options
        };

        const response = await fetch(url, config);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));

            throw new Error(errorData.message || `HTTP ${ response.status }: ${ response.statusText }`);
        }

        // attempt to parse JSON body, but tolerate empty responses
        const data = await response.json().catch(() => null);

        const apiVersion = response.headers.get('X-API-Version') || 'N/A';

        // If the body is a plain object, merge apiVersion into it so callers keep the existing shape.
        // If it's an array or primitive (or null), return it under `data` to avoid surprising spreads.
        if (data && typeof data === 'object' && !Array.isArray(data)) {
            return { ...data, apiVersion };
        }

        return data;
    } catch (error) {
        console.error(`API request failed for ${ endpoint }:`, error);

        throw error;
    }
};

// Dashboard API functions
export const dashboardAPI = {
    /**
     * Get queue status
     */
    getQueueStatus: (token) => apiRequest('/api/v1/notify/dashboard/status', {}, token),

    /**
     * Get system status
     */
    getSystemStatus: (token) => apiRequest('/api/v1/notify/dashboard/system', {}, token),

    /**
     * Get queue messages
     * @param {object} params - Query parameters
     */
    getMessages: (params = {}, token) => {
        const queryString = new URLSearchParams(params).toString();
        return apiRequest(`/api/v1/notify/dashboard/messages${ queryString ? `?${ queryString }` : '' }`, {}, token);
    },

    /**
     * Get monitored shifts
     */
    getShifts: (token) => apiRequest('/api/v1/notify/dashboard/shifts', {}, token),

    /**
     * Get notification preferences
     */
    getPreferences: (token) => apiRequest('/api/v1/notify/dashboard/preferences', {}, token),

    /**
     * Process queue manually
     */
    processQueue: (token) => apiRequest('/api/v1/notify/dashboard/process', { method: 'POST' }, token),

    /**
     * Archive old messages
     */
    archiveMessages: (token) => apiRequest('/api/v1/notify/dashboard/archive', { method: 'POST' }, token),

    /**
     * Clear failed messages
     */
    clearFailedMessages: (token) => apiRequest('/api/v1/notify/dashboard/clear-failed', { method: 'POST' }, token),

    /**
     * Retry specific message
     * @param {string} messageId - Message ID to retry
     */
    retryMessage: (messageId, token) => apiRequest(`/api/v1/notify/dashboard/retry/${ messageId }`, { method: 'POST' }, token),

    /**
     * Delete specific message
     * @param {string} messageId - Message ID to delete
     */
    deleteMessage: (messageId, token) => apiRequest(`/api/v1/notify/dashboard/messages/${ messageId }`, { method: 'DELETE' }, token)
};
